'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import { useCallback, useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { observer, useComputed } from '@legendapp/state/react';
import { batch } from '@legendapp/state';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

import ROUTES from '@/lib/constants/routeConfig';

import { useAutosizeTextArea, useFormStore } from '@/lib/hooks';
import { getMockExperts, logger } from '@/lib/utils';
import { useI18n } from '@/locales/client';
import { ConversationResource, FormAnswer } from '@/types';
import {
  adviceState,
  adviceActions,
  adviceSelectors,
} from '@/lib/store/advice.store';
import { sidebarActions } from '@/lib/store/sidebar.store';
import { MetricsCategory, MetricsAction } from '@/lib/utils/metrics.utils';
import { useMetrics } from '@/lib/hooks/useMetrics';

import {
  ConversationContent,
  // ExcludedSituationModal,
  InputArea,
  RightSidebar,
  SuccessMessage,
  WelcomeContent,
} from '@/components/features/chat/components';
import { Sparkles, Users } from 'lucide-react';
import { featureFlagsSelectors } from '@/lib/store/feature-flags.store';

const isDemo =
  typeof window !== 'undefined' && window.location.href.includes('demo');

export const ChatContainer = observer(({}) => {
  const { data: session, status } = useSession();
  const queryClient = useQueryClient();
  const t = useI18n();
  const router = useRouter();
  const searchParams = useSearchParams();
  const chatId = searchParams?.get('chatId');

  const page = chatId ? 'chat' : 'home';

  // Add metrics tracking
  const { trackEvent, trackFeature } = useMetrics();

  // Refs for DOM nodes (used for scrolling, autosizing, etc.)
  const recentPromptRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  // Local state for input, questionnaire, and modals.
  const [inputValue, setInputValue] = useState('');

  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  // Commented out the ones below as they are npt being used
  // const [isExcludedSituationModalOpen, setIsExcludedSituationModalOpen] =
  //   useState(false);
  // const [excludedSituationFeedback, setExcludedSituationFeedback] =
  //   useState('');
  const [showExcludedSuccessMsg, setShowExcludedSuccessMsg] = useState(false);

  // Form state from your custom hook.
  const {
    formData,
    formAnswerId,
    isQuestionnaireSubmitted,
    setQuestionnaireSubmitted,
    setQuestionnaireSubmission,
    setFormAnswerId,
    isLoading: isFormLoading,
  } = useFormStore();

  // Subscribe to combined store state using useComputed.
  const storeState = useComputed(() => ({
    isPending:
      adviceState.status.get() === 'submitted' ||
      adviceState.status.get() === 'streaming',
    isTransitioning: adviceState.isTransitioning.get(),
    currentChatId: adviceState.currentChatId.get(),
    error: adviceState.error.get(),
    activeConversation: adviceState.activeConversation.get(),
    advices: adviceState.advices.get(),
  }));

  // Auto-size the prompt text area.
  useAutosizeTextArea(promptInputRef.current, inputValue);

  // Computed loading state: true if the store is pending/transitioning or if a conversation is being fetched.
  const isLoadingState = useComputed(() => {
    const state = storeState.get();
    return !!(
      state.isPending ||
      state.isTransitioning ||
      queryClient.isFetching({
        queryKey: ['conversation', state.currentChatId],
      })
    );
  });

  // Computed error state.
  const isErrorState = useComputed(() => storeState.get().error !== null);

  // Computed welcome state: show welcome if there's no active conversation (or messages), no errors, and not loading.
  const shouldShowWelcome = useComputed(() => {
    const state = storeState.get();
    const loading = isLoadingState.get();
    const error = isErrorState.get();

    return (
      !error &&
      (!state.activeConversation || !state.advices.length) &&
      !loading &&
      !state.currentChatId
    );
  });

  // Track last navigation to prevent duplicate URL updates.
  const lastNavigationRef = useRef<string | null>(null);

  // Add state for right sidebar visibility
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);

  // Standardized reset function.
  const handleReset = useCallback(
    async (options: { showTransition?: boolean } = {}) => {
      const { showTransition = true } = options;
      try {
        if (showTransition) {
          adviceActions.startTransition();
        }
        const controller = adviceState.activeController.get();
        if (controller instanceof AbortController) {
          controller.abort('RESET_CHAT');
        }

        // Track conversation end if there's an active conversation
        const currentConversation = adviceSelectors.getActiveConversation();
        if (currentConversation?.id) {
          trackEvent({
            category: MetricsCategory.CONVERSATION,
            action: MetricsAction.CONVERSATION_ENDED,
            label: 'Reset Chat',
            dimensions: {
              conversationId: currentConversation.id,
              messageCount: currentConversation.advices?.length || 0,
            },
          });
        }

        adviceActions.reset();
        adviceActions.completeRequest();
        adviceActions.setActiveConversation(null);
        adviceActions.setCurrentChatId(null);
        lastNavigationRef.current = null;
        router.replace('/', { scroll: false });
        if (showTransition) {
          adviceActions.completeTransition();
        }
      } catch (error: any) {
        if (error.name === 'AbortError') return;
        logger.error(error);

        // Track error
        trackEvent({
          category: MetricsCategory.ERROR,
          action: MetricsAction.CLIENT_ERROR,
          label: 'Reset Chat Error',
          dimensions: { error: error.message },
        });

        if (showTransition) {
          adviceActions.completeTransition();
        }
      }
    },
    [router, trackEvent]
  );

  // Handle prompt submission.
  const handlePromptSubmission = useCallback(
    async (promptText: string, formAnswerId?: string) => {
      const state = storeState.get();
      if (!session || !promptText.trim() || !formAnswerId) {
        logger.error(
          '[ChatContainer:handlePromptSubmission] Missing required parameters',
          {
            hasSession: Boolean(session),
            promptLength: promptText?.length,
            formAnswerId,
          }
        );
        toast.error('Cannot submit message: Missing form context.');
        return;
      }

      logger.debug(
        '[ChatContainer:handlePromptSubmission] Starting submission',
        {
          hasSession: Boolean(session),
          promptLength: promptText.length,
          currentState: state,
          formAnswerId,
        }
      );

      try {
        setInputValue('');
        adviceActions.startRequest(true);

        // Determine if an active conversation exists.
        let currentChatId = adviceSelectors.getActiveConversation()?.id;
        const chatIdParam = searchParams?.get('chatId');

        logger.debug('[ChatContainer:handlePromptSubmission] Chat context', {
          currentChatId,
          chatIdParam,
          hasExistingConversation: Boolean(currentChatId),
          formAnswerId,
        });

        // Invalidate queries to refresh data.
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
        queryClient.invalidateQueries({
          queryKey: ['conversation', currentChatId],
        });

        // Track conversation metrics
        const isNewConversation =
          !currentChatId || (chatIdParam && currentChatId !== chatIdParam);

        if (isNewConversation) {
          if (!chatIdParam) {
            logger.debug(
              '[ChatContainer:handlePromptSubmission] Creating new conversation'
            );

            // Track conversation start
            const startTime = Date.now();

            const newConversation = await adviceActions.createConversation(
              promptText,
              formAnswerId
            );
            currentChatId = newConversation.id;

            // Track conversation created event with response time
            trackEvent({
              category: MetricsCategory.CONVERSATION,
              action: MetricsAction.CONVERSATION_STARTED,
              label: 'New Conversation',
              value: Date.now() - startTime, // Time to create conversation in ms
              dimensions: { conversationId: currentChatId },
            });

            router.replace(`?chatId=${currentChatId}`, { scroll: false });

            logger.debug(
              '[ChatContainer:handlePromptSubmission] Created new conversation',
              {
                newChatId: currentChatId,
              }
            );
          } else {
            currentChatId = chatIdParam;
            logger.debug(
              '[ChatContainer:handlePromptSubmission] Using existing chat from URL',
              { chatId: currentChatId }
            );
          }
        }

        logger.debug(
          '[ChatContainer:handlePromptSubmission] Submitting message',
          {
            chatId: currentChatId,
            promptLength: promptText.length,
          }
        );

        // Track conversation message
        trackEvent({
          category: MetricsCategory.CONVERSATION,
          action: MetricsAction.CONVERSATION_MESSAGE,
          label: 'User Message (Server Action)',
          dimensions: {
            conversationId: currentChatId,
            messageType: 'user',
            messageLength: promptText.length,
          },
        });

        // Track response time
        const requestStartTime = Date.now();

        await adviceActions.submitMessage(
          promptText,
          currentChatId as string,
          'en',
          formData as unknown as FormAnswer
        );

        // Track frontend response time
        const responseTime = Date.now() - requestStartTime;
        trackEvent({
          category: MetricsCategory.PERFORMANCE,
          action: MetricsAction.FRONTEND_RESPONSE,
          label: 'Message Response Time',
          value: responseTime,
          dimensions: {
            conversationId: currentChatId,
            messageLength: promptText.length,
          },
        });

        logger.debug(
          '[ChatContainer:handlePromptSubmission] Message submitted successfully'
        );
      } catch (err) {
        logger.error('[ChatContainer:handlePromptSubmission] Error:', err);
        adviceActions.failRequest(err as Error);

        // Track error
        trackEvent({
          category: MetricsCategory.ERROR,
          action: MetricsAction.CLIENT_ERROR,
          label: 'Message Submission Error',
          dimensions: {
            error: (err as Error).message,
            errorType: (err as Error).name,
          },
        });

        toast.error('Failed to send message', {
          description: 'Please try again',
        });
      }
    },
    [
      session,
      queryClient,
      router,
      searchParams,
      storeState,
      trackEvent,
      formData,
    ]
  );

  const handleNewChat = useCallback(async () => {
    try {
      // Cancel any active controller
      const controller = adviceState.activeController.get();
      if (controller instanceof AbortController) {
        controller.abort('RESET_CHAT');
        adviceActions.setActiveController(null);
      }

      // Track conversation end if there's an active conversation
      const currentConversation = adviceSelectors.getActiveConversation();
      if (currentConversation?.id) {
        trackEvent({
          category: MetricsCategory.CONVERSATION,
          action: MetricsAction.CONVERSATION_ENDED,
          label: 'New Chat',
          dimensions: {
            conversationId: currentConversation.id,
            messageCount: currentConversation.advices?.length || 0,
          },
        });
      }

      // Track new chat event
      trackFeature('New Chat Button');

      batch(() => {
        adviceActions.reset();
        sidebarActions.resetSidebarState();
      });

      // Force immediate query cache reset
      queryClient.removeQueries({ queryKey: ['conversation'] });

      // Navigate to home
      router.replace('/', { scroll: false });
    } catch (error) {
      toast.error('Failed to start new chat');

      // Track error
      trackEvent({
        category: MetricsCategory.ERROR,
        action: MetricsAction.CLIENT_ERROR,
        label: 'New Chat Error',
        dimensions: { error: (error as Error).message },
      });
    }
  }, [router, queryClient, trackEvent, trackFeature]);

  // Handle form go-back (for questionnaire)
  const handleFormGoBack = useCallback(() => {
    setShowQuestionnaire(true);
    setQuestionnaireSubmitted(false);
    setQuestionnaireSubmission('none');
  }, [setQuestionnaireSubmitted, setQuestionnaireSubmission]);

  const shouldShowAIToolsBanner =
    status === 'authenticated' &&
    featureFlagsSelectors.isFeatureEnabled('AI_TOOLS');

  const shouldShowHireExpertsSidePanel =
    status === 'authenticated' &&
    featureFlagsSelectors.isFeatureEnabled('MARKETPLACE');

  const shouldShowRightSidebar =
    status === 'authenticated' &&
    (shouldShowAIToolsBanner || shouldShowHireExpertsSidePanel);

  // Optionally, you could synchronize the URL with the store here if needed.
  // (Currently commented out.)

  // Listen for follow-up question clicks
  useEffect(() => {
    const handleFollowUpClick = (event: CustomEvent<{ question: string }>) => {
      const { question } = event.detail;
      if (question && formAnswerId) {
        // Set the input value and trigger submission
        setInputValue(question);
        // Small delay to ensure state is updated
        setTimeout(() => {
          handlePromptSubmission(question, formAnswerId);
        }, 100);
      }
    };

    document.addEventListener('followUpQuestionClicked', handleFollowUpClick as EventListener);

    return () => {
      document.removeEventListener('followUpQuestionClicked', handleFollowUpClick as EventListener);
    };
  }, [formAnswerId, handlePromptSubmission, setInputValue]);

  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      <div className='relative flex min-h-0 flex-1 overflow-hidden'>
        {/* Main chat area with flex-grow */}
        <motion.div
          className={clsx(
            'flex min-h-0 flex-1 flex-col overflow-hidden bg-background transition-all duration-200 ease-in-out',
            isRightSidebarOpen ? 'w-full xl:w-[calc(100%-380px)]' : 'w-full'
          )}
        >
          <div className='relative flex min-h-0 flex-1 flex-col overflow-hidden'>
            <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
              {/* <div className='relative flex h-[calc(100dvh-72px)] max-h-[calc(100dvh-72px)] min-h-0 flex-1 flex-col overflow-hidden'> */}
              {/* Welcome content */}
              {shouldShowWelcome.get() && (
                <WelcomeContent
                  isFormLoading={isFormLoading}
                  isQuestionnaireSubmitted={isQuestionnaireSubmitted}
                  savedFormData={formData}
                  isDemo={isDemo}
                  isChatLoading={storeState.get().isPending}
                  handlePromptSubmission={handlePromptSubmission}
                  formAnswerId={formAnswerId}
                  t={t}
                />
              )}

              {/* Conversation content */}
              {!shouldShowWelcome.get() && (
                <div className='flex min-h-0 flex-1 overflow-y-auto'>
                  <ConversationContent
                    recentPromptRef={recentPromptRef}
                    chatContainerRef={chatContainerRef}
                  />
                </div>
              )}
            </div>

            {/* Input area and mobile tools */}
            <div className='sticky bottom-0 left-0 right-0 flex-shrink-0'>
              <div className='bg-background'>
                <InputArea
                  isQuestionnaireSubmitted={isQuestionnaireSubmitted}
                  conversationData={storeState.get().activeConversation}
                  handleFormGoBack={handleFormGoBack}
                  inputValue={inputValue}
                  setInputValue={setInputValue}
                  handlePromptSubmission={handlePromptSubmission}
                  formAnswerId={formAnswerId}
                  t={t}
                  isChatLoading={storeState.get().isPending}
                  isStreaming={adviceState.isStreaming.get()}
                  promptInputRef={promptInputRef}
                />
                <div className='px-4 py-2 text-center text-sm text-muted-foreground'>
                  ⚠️ {t('policies.disclaimer.text')}{' '}
                  <a
                    href={'/policies?tab=terms'}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='m-0 inline cursor-pointer border-none bg-transparent p-0 text-primary underline hover:text-primary/80'
                  >
                    {t('policies.disclaimer.policy')}
                  </a>
                  .
                </div>
              </div>
              {/* Mobile tools : only show if we would show the sidebar*/}
              {page === 'chat' &&
                adviceSelectors.getActiveConversation()?.caseOutcome &&
                shouldShowRightSidebar && (
                  <AnimatePresence>
                    {(shouldShowAIToolsBanner ||
                      shouldShowHireExpertsSidePanel) && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className='flex w-full border-t border-gray-200 bg-white p-2 dark:border-gray-700/50 dark:bg-gray-800 xl:hidden'
                      >
                        <div className='grid w-full grid-cols-2 gap-2'>
                          {shouldShowAIToolsBanner && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => router.push('/ai-tools/dashboard')}
                              className='flex items-center justify-center gap-2 rounded-lg bg-purple-50 px-4 py-2 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 dark:hover:bg-purple-900/50'
                            >
                              <Sparkles className='h-4 w-4' />
                              <span>{t('navigation.aiTools')}</span>
                            </motion.button>
                          )}
                          {shouldShowHireExpertsSidePanel}
                          {shouldShowHireExpertsSidePanel && (
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={() => router.push('/marketplace')}
                              className='flex items-center justify-center gap-2 rounded-lg bg-purple-50 px-4 py-2 text-sm font-medium text-purple-600 transition-colors hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-400 dark:hover:bg-purple-900/50'
                            >
                              <Users className='h-4 w-4' />
                              <span>{t('experts.title')}</span>
                            </motion.button>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
            </div>
          </div>
        </motion.div>

        {/* Right sidebar */}
        {page === 'chat' &&
          adviceSelectors.getActiveConversation()?.caseOutcome &&
          shouldShowRightSidebar && (
            <AnimatePresence>
              <motion.div className='hidden xl:block'>
                <RightSidebar
                  legalExperts={getMockExperts()}
                  isOpen={isRightSidebarOpen}
                  showAIToolsBanner={shouldShowAIToolsBanner}
                  showHireExpertsSidePanel={shouldShowHireExpertsSidePanel}
                />
              </motion.div>
            </AnimatePresence>
          )}
      </div>

      <SuccessMessage
        show={showExcludedSuccessMsg}
        onClose={() => setShowExcludedSuccessMsg(false)}
        message={t(
          'formQuestionnaire.messages.excludedSituation.submissionSuccess'
        )}
      />
    </div>
  );
});
